import type { IncidentGroup } from '../../services/incidentsApi';
import { readPatternOwnerLabel } from './incidentOwnershipSemantics';

export type IncidentOpsQueueFilter =
  | ''
  | 'my_queue'
  | 'my_risk_queue'
  | 'assigned_by_me'
  | 'risk_assigned_by_me'
  | 'escalations'
  | 'overdue'
  | 'unassigned_high_risk';

export const INCIDENT_OWNERSHIP_QUEUE_OPTIONS: Array<{
  value: IncidentOpsQueueFilter;
  label: string;
  helper: string;
}> = [
  {
    value: 'my_queue',
    label: 'Assigned to me (action)',
    helper: 'Open items where you are the action / follow-up owner.',
  },
  {
    value: 'my_risk_queue',
    label: 'Assigned to me (risk)',
    helper: 'Recurring risks where you are the accountable risk owner.',
  },
  {
    value: 'assigned_by_me',
    label: 'Assigned by me (action)',
    helper: 'Open items where you assigned the action owner to someone else.',
  },
  {
    value: 'risk_assigned_by_me',
    label: 'Assigned by me (risk)',
    helper: 'Recurring risks where you assigned the risk owner.',
  },
];

export function resolveOperatorActorId(
  user: { email?: string | null; username?: string | null } | null | undefined,
): string {
  return String(user?.email || user?.username || '').trim();
}

export function ownershipQueueRequiresActor(
  queue: IncidentOpsQueueFilter,
): queue is 'my_queue' | 'my_risk_queue' | 'assigned_by_me' | 'risk_assigned_by_me' {
  return (
    queue === 'my_queue'
    || queue === 'my_risk_queue'
    || queue === 'assigned_by_me'
    || queue === 'risk_assigned_by_me'
  );
}

/** Action-owner queues need raw incident rows; risk-owner queues can filter Summary groups. */
export function ownershipQueueRequiresRawTable(queue: IncidentOpsQueueFilter): boolean {
  return (
    queue === 'my_queue'
    || queue === 'assigned_by_me'
    || queue === 'escalations'
    || queue === 'overdue'
    || queue === 'unassigned_high_risk'
  );
}

export function ownershipQueueUsesGroupedClientFilter(queue: IncidentOpsQueueFilter): boolean {
  return queue === 'my_risk_queue' || queue === 'risk_assigned_by_me';
}

export function isIncidentOpsQueueFilter(value: string): value is IncidentOpsQueueFilter {
  return (
    value === ''
    || value === 'my_queue'
    || value === 'my_risk_queue'
    || value === 'assigned_by_me'
    || value === 'risk_assigned_by_me'
    || value === 'escalations'
    || value === 'overdue'
    || value === 'unassigned_high_risk'
  );
}

function normalizeActor(value: string): string {
  return String(value || '').trim().toLowerCase();
}

export function groupMatchesOwnershipQueue(
  group: IncidentGroup | null | undefined,
  queue: 'my_risk_queue' | 'risk_assigned_by_me',
  actorId: string,
): boolean {
  const actor = normalizeActor(actorId);
  if (!actor || !group) return false;
  if (queue === 'my_risk_queue') {
    const owner = normalizeActor(readPatternOwnerLabel(group as any));
    return Boolean(owner) && owner === actor;
  }
  const assignedBy = normalizeActor(String(group.canonical_pattern_assigned_by || '').trim());
  return Boolean(assignedBy) && assignedBy === actor;
}

export function filterGroupsForOwnershipQueue(
  groups: IncidentGroup[],
  queue: IncidentOpsQueueFilter,
  actorId: string,
): IncidentGroup[] {
  if (!ownershipQueueUsesGroupedClientFilter(queue) || !actorId) return groups;
  const groupedQueue = queue as 'my_risk_queue' | 'risk_assigned_by_me';
  return (groups || []).filter((group) =>
    groupMatchesOwnershipQueue(group, groupedQueue, actorId),
  );
}
